import { EXAM_RULES } from "@eb/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QuestionView } from "../components/QuestionView";
import { Button, Card, ErrorBanner, Spinner, cx } from "../components/ui";
import { api } from "../lib/api";
import { formatClock } from "../lib/format";

/** Below this many seconds the clock turns red. */
const URGENT_SECONDS = 300;

export function ExamRunnerPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const examQuery = useQuery({
        queryKey: ["exam", id],
        queryFn: () => api.exams.get(id!),
        enabled: Boolean(id),
        // The session is authoritative on the server; do not silently re-fetch and
        // clobber answers the learner has queued locally.
        staleTime: Infinity
    });

    const [position, setPosition] = useState(1);
    const [answers, setAnswers] = useState<Record<number, number | null>>({});
    const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
    const [confirming, setConfirming] = useState(false);

    // Seed local answers from the server, so resuming an exam restores what was picked.
    useEffect(() => {
        if (!examQuery.data) return;
        const seeded: Record<number, number | null> = {};
        for (const item of examQuery.data.questions) seeded[item.position] = item.selectedOptionId;
        setAnswers(seeded);
    }, [examQuery.data]);

    const expiresAt = examQuery.data ? new Date(examQuery.data.expiresAt).getTime() : null;

    const submitMutation = useMutation({
        mutationFn: () => api.exams.submit(id!),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ["exams"] });
            void queryClient.invalidateQueries({ queryKey: ["stats"] });
            void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
            navigate(`/exam/${id}/result`, { replace: true });
        }
    });

    const submit = useCallback(() => {
        if (!submitMutation.isPending) submitMutation.mutate();
    }, [submitMutation]);

    // Countdown. When it hits zero the client submits; the server enforces the same
    // deadline independently, so a closed tab still gets graded on the next visit.
    useEffect(() => {
        if (expiresAt === null) return;
        const tick = () => {
            const remaining = Math.round((expiresAt - Date.now()) / 1000);
            setSecondsLeft(remaining);
            if (remaining <= 0) submit();
        };
        tick();
        const timer = setInterval(tick, 1000);
        return () => clearInterval(timer);
    }, [expiresAt, submit]);

    const answerMutation = useMutation({
        mutationFn: ({ position: pos, optionId }: { position: number; optionId: number | null }) =>
            api.exams.answer(id!, pos, optionId)
    });

    const handleSelect = useCallback(
        (optionId: number) => {
            setAnswers((previous) => ({ ...previous, [position]: optionId }));
            answerMutation.mutate({ position, optionId });
        },
        [answerMutation, position]
    );

    const answeredCount = useMemo(
        () => Object.values(answers).filter((value) => value !== null && value !== undefined).length,
        [answers]
    );

    // Already-finished exams belong on the result page.
    useEffect(() => {
        if (examQuery.data?.finishedAt) navigate(`/exam/${id}/result`, { replace: true });
    }, [examQuery.data?.finishedAt, id, navigate]);

    if (examQuery.isLoading) return <Spinner label="正在准备试卷" />;
    if (examQuery.isError || !examQuery.data) {
        return <ErrorBanner message="加载试卷失败" onRetry={() => void examQuery.refetch()} />;
    }

    const current = examQuery.data.questions.find((item) => item.position === position);
    const total = examQuery.data.questions.length;
    const urgent = secondsLeft !== null && secondsLeft <= URGENT_SECONDS;

    return (
        <div className="space-y-5">
            <header className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-lg font-semibold text-ink">模拟考进行中</h1>
                    <p className="text-sm text-ink-soft">
                        已作答 {answeredCount} / {total} 题 · 答对 {EXAM_RULES.passingScore} 题通过
                    </p>
                </div>
                <div
                    className={cx(
                        "flex items-center gap-2 rounded-xl border px-3 py-2 font-mono text-lg tabular-nums",
                        urgent ? "border-bad bg-bad-soft text-bad" : "border-line bg-surface text-ink"
                    )}
                    role="timer"
                    aria-live={urgent ? "polite" : "off"}
                >
                    <Clock className="size-4" aria-hidden />
                    {secondsLeft === null ? "--:--" : formatClock(secondsLeft)}
                </div>
            </header>

            {/* Question map: lets the learner skip around and see what is still blank. */}
            <div className="flex flex-wrap gap-1.5">
                {examQuery.data.questions.map((item) => {
                    const isAnswered = answers[item.position] != null;
                    const isCurrent = item.position === position;
                    return (
                        <button
                            key={item.position}
                            onClick={() => setPosition(item.position)}
                            aria-current={isCurrent}
                            aria-label={`第 ${item.position} 题${isAnswered ? "，已作答" : "，未作答"}`}
                            className={cx(
                                "size-8 rounded-lg border text-xs font-medium tabular-nums transition-colors",
                                isCurrent
                                    ? "border-brand bg-brand text-white"
                                    : isAnswered
                                      ? "border-brand/40 bg-brand-soft text-brand"
                                      : "border-line bg-surface text-ink-soft hover:border-brand"
                            )}
                        >
                            {item.position}
                        </button>
                    );
                })}
            </div>

            {current ? (
                <Card className="p-5 sm:p-7">
                    <p className="mb-3 text-sm text-ink-soft">
                        第 {position} / {total} 题
                    </p>
                    <QuestionView
                        question={current.question}
                        selectedOptionId={answers[position] ?? null}
                        onSelect={handleSelect}
                    />
                </Card>
            ) : null}

            <div className="flex items-center justify-between gap-3">
                <Button
                    variant="secondary"
                    disabled={position <= 1}
                    onClick={() => setPosition((value) => Math.max(1, value - 1))}
                >
                    <ChevronLeft className="size-4" aria-hidden />
                    上一题
                </Button>

                {position < total ? (
                    <Button variant="secondary" onClick={() => setPosition((value) => Math.min(total, value + 1))}>
                        下一题
                        <ChevronRight className="size-4" aria-hidden />
                    </Button>
                ) : (
                    <Button onClick={() => setConfirming(true)}>交卷</Button>
                )}
            </div>

            {position < total ? (
                <div className="text-center">
                    <button
                        onClick={() => setConfirming(true)}
                        className="text-sm text-ink-soft underline underline-offset-2 hover:text-ink"
                    >
                        提前交卷
                    </button>
                </div>
            ) : null}

            {confirming ? (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
                    <Card className="w-full max-w-sm space-y-4 p-6">
                        <h2 className="text-base font-semibold text-ink">确认交卷？</h2>
                        <p className="text-sm text-ink-soft">
                            还有 {total - answeredCount} 题没有作答，未作答按错误计算。交卷后不能再修改。
                        </p>
                        <div className="flex justify-end gap-2">
                            <Button variant="secondary" onClick={() => setConfirming(false)}>
                                再检查一下
                            </Button>
                            <Button onClick={submit} loading={submitMutation.isPending}>
                                确认交卷
                            </Button>
                        </div>
                    </Card>
                </div>
            ) : null}
        </div>
    );
}
